import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Alert, Button, Modal, Table, TextInput } from '@opendate-app/setlist';

const plural = (count, word) => `${count.toLocaleString()} ${word}${count === 1 ? '' : 's'}`;
const verb = (count, singular, pluralVerb) => (count === 1 ? singular : pluralVerb);

const columns = [
  { key: 'seat', header: 'Seat', render: (row) => [row.section, row.label].filter(Boolean).join(' · ') },
  { key: 'count', header: 'Tickets', align: 'right', render: (row) => row.count.toLocaleString() }
];

const SeatMapSwapPreviewModal = ({ preview, onClose, onSwitch }) => {
  const [typed, setTyped] = useState('');
  useEffect(() => setTyped(''), [preview]);
  const needsNewSeat = preview?.needs_new_seat || [];
  const movingCount = needsNewSeat.reduce((sum, row) => sum + row.count, 0);
  const checkouts = preview?.checkouts_in_progress || 0;
  const soldCount = (preview?.kept_count || 0) + movingCount;
  const canSwitch = soldCount > 0 && movingCount === 0 && checkouts === 0 && !preview?.seated_add_ons_sold;

  return (
    <Modal
      open={!!preview}
      title={preview?.map_name ? `Seats on ${preview.map_name}` : 'Seats on the new map'}
      onClose={onClose}
      footer={canSwitch ? (
        <React.Fragment>
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button variant="primary" disabled={typed.trim().toUpperCase() !== 'SWAP'} onClick={onSwitch}>Switch seat map</Button>
        </React.Fragment>
      ) : <Button variant="primary" onClick={onClose}>Got it</Button>}
    >
      {preview?.seated_add_ons_sold ? (
        <p className="mb-0" style={{ fontSize: '14px' }}>
          This show has sold seated add-ons (like tables or parking), so its seat map can&apos;t be switched yet.
        </p>
      ) : preview && soldCount === 0 && checkouts === 0 ? (
        <p className="mb-0" style={{ fontSize: '14px' }}>
          No admission seats are sold and no one is checking out. Reload the page and try again.
        </p>
      ) : preview && soldCount === 0 ? (
        <p className="mb-0" style={{ fontSize: '14px' }}>
          {checkouts.toLocaleString()} {verb(checkouts, 'person', 'people')} {verb(checkouts, 'is', 'are')} checking out right now. Reload the page once they finish.
        </p>
      ) : preview ? (
        <div style={{ fontSize: '14px' }}>
          {canSwitch ? null : (
            <p className="mb-2">
              The seat map can switch once every sold ticket has a matching seat on the new map and no one is checking out. Nothing has changed yet.
            </p>
          )}
          <p className="mb-2">
            {plural(preview.kept_count, 'ticket')} {verb(preview.kept_count, 'keeps', 'keep')} the same seat on the new map.
          </p>
          {movingCount > 0 ? (
            <React.Fragment>
              <p className="mb-2">
                {plural(movingCount, 'ticket')} {verb(movingCount, 'needs', 'need')} a seat that matches on the new map.
              </p>
              <div style={{ maxHeight: '320px', overflowY: 'auto' }}>
                <Table
                  columns={columns}
                  data={needsNewSeat}
                  getRowKey={(row) => `${row.section}-${row.label}`}
                />
              </div>
            </React.Fragment>
          ) : (
            <p className="mb-2">Every ticket sold has a matching seat on the new map.</p>
          )}
          {canSwitch ? (
            <React.Fragment>
              <Alert
                className="mb-2"
                color="warning"
                size="full-width"
                title="Fans keep their tickets, seats, wallet passes and door scans, and no money moves. Kills and holds are cleared, so re-apply them on the new map."
              />
              <TextInput label="Type SWAP to switch" value={typed} onChange={(e) => setTyped(e.target.value)} />
            </React.Fragment>
          ) : null}
          {checkouts > 0 ? (
            <p className="mt-2 mb-0">
              {checkouts.toLocaleString()} {verb(checkouts, 'person', 'people')} {verb(checkouts, 'is', 'are')} checking out right now.
            </p>
          ) : null}
        </div>
      ) : null}
    </Modal>
  );
};

SeatMapSwapPreviewModal.propTypes = {
  preview: PropTypes.shape({
    map_name: PropTypes.string,
    seating_chart: PropTypes.shape({ id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]) }),
    kept_count: PropTypes.number.isRequired,
    checkouts_in_progress: PropTypes.number.isRequired,
    seated_add_ons_sold: PropTypes.bool,
    needs_new_seat: PropTypes.arrayOf(PropTypes.shape({
      section: PropTypes.string,
      label: PropTypes.string,
      count: PropTypes.number
    })).isRequired
  }),
  onClose: PropTypes.func.isRequired,
  onSwitch: PropTypes.func.isRequired
};

SeatMapSwapPreviewModal.defaultProps = {
  preview: null
};

export default SeatMapSwapPreviewModal;
